import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';
import React, {useState} from 'react';

import {assert, assertAndContinue} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {Notation} from 'biojs/BioConstants';
import {FrontendLabelingStrategy, RegistryLabelingStrategy, TagSchema} from 'constants/RegistryConstants';
import {TagSchemaSequenceSchemaType} from 'graphql-client/__generated/graphqlGlobalTypes';
import BenchlingQuery, {
  QueryResultType,
  SkippableBenchlingQueryResult,
  useSkippableBenchlingQuery,
} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import ResidueNotationDropdown from 'molbio/ResidueNotationDropdown';
import {RegistrationStructuredTableAdapter} from 'notebook/integrations/registry/RegistrationStructuredTableAdapter';
import {RegistrationTableInsertType} from 'notebook/integrations/registry/RegistrationTableConstants';
import {RegistrationTableType} from 'notebook/registration-tables/RegistrationTableConstants';
import {RegistrationTableSchemaFieldSelectorsQuery} from 'notebook/structured-tables/components/modals/__generated/RegistrationTableSchemaFieldSelectorsQuery';
import useSelectFolderOptionForTable, {
  StructuredTableFolderOption,
} from 'notebook/structured-tables/components/useSelectFolderOptionForTable';
import {SchemaFieldSelectorsDict} from 'notebook/structured-tables/hooks/useSelectorsById';
import {StructuredTableInsertionContextValue} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import isBatchesEnabled from 'registry/IsBatchesEnabled';
import {
  getDefaultLabelingStrategy,
  getEnabledLabelingStrategies,
} from 'registry/labeling-strategies/LabelingStrategiesUtil';
import SchemaLabelingStrategyInput from 'registry/labeling-strategies/SchemaLabelingStrategyInput';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import {isTagSchema} from 'schemas/SchemaPredicates';
import ModalButtonKey from 'test/lib/cypress/shared/ModalButtonKey';
import FormGroup from 'ui-library/bs/FormGroup';
import {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Modal from 'ui-library/modals/Modal';
import ToggleGroup from 'ui-library/ToggleGroup';

import {
  RegistrationTableSchemaInterfacesQuery,
  RegistrationTableSchemaInterfacesQueryVariables,
  RegistrationTableSchemaInterfacesQuery_complexPolymerSchemaConfiguration_GenericComplexPolymerSchemaConfigNode_OligoConjugateSchemaConfigNode_OligoDuplexSchemaConfigNode as ImplementedComplexPolymerInterface,
  RegistrationTableSchemaInterfacesQuery_implementedAntibodySchemaInterface_ImplementedAntibodySchemaInterfaceNode as ImplementedAntibodyInterface,
} from './__generated/RegistrationTableSchemaInterfacesQuery';
import {RegistrationTablePreview} from './RegistrationTablePreview';
import {
  RegistrationTableKind,
  RegistrationTableTagSchemaSelector,
} from './RegistrationTableTagSchemaSelector';

export type ImplementedStaticSchemaInterfaceGQL =
  | ImplementedComplexPolymerInterface
  | ImplementedAntibodyInterface;

export interface RegistrationTableInsertModalProps {
  adapter: RegistrationStructuredTableAdapter;

  // The callback for when the Modal is closed
  onRequestClose: () => void;

  // The callback for the legacy Reg table the submit button on the Modal is pressed
  onLegacyRequestSubmit: (args: {
    tagSchema: TagSchema;
    tableType: string;
    labelingStrategy: string | null;
    selectorsById: SchemaFieldSelectorsDict;
  }) => void;

  // The callback for the new Reg table
  onRequestSubmit: (args: {
    schema: TagSchema;
    insertType: RegistrationTableInsertType;
    labelingStrategy: RegistryLabelingStrategy;
    selectorsById: SchemaFieldSelectorsDict;
    implementedSchemaInterfaces: Array<ImplementedStaticSchemaInterfaceGQL>;
    notation: Notation | null;
    customSyntaxName?: string | null;
    folderOption?: StructuredTableFolderOption;
    folderId?: string;
  }) => void;

  selectorsById: SchemaFieldSelectorsDict | null;

  schemaSelected: TagSchema | null;
  setSchemaSelected: (schema: TagSchema | null) => void;

  implementedSchemaInterfaces: Array<ImplementedStaticSchemaInterfaceGQL>;

  structuredTableInsertionContext: StructuredTableInsertionContextValue;
}

const _ENABLED_INSERT_TYPE_KEYS = isBatchesEnabled()
  ? (Object.keys(RegistrationTableInsertType) as Array<keyof typeof RegistrationTableInsertType>)
  : (Object.keys(RegistrationTableInsertType).filter((key) => key !== 'LEGACY_REGISTER_BATCHES') as Array<
      keyof typeof RegistrationTableInsertType
    >);

const _TOGGLE_BUTTON_OPTIONS = _ENABLED_INSERT_TYPE_KEYS.map(
  (key: keyof typeof RegistrationTableInsertType) => ({
    buttonProps: {
      text: RegistrationTableInsertType[key],
    },
    dataTestAttrs: {
      component: 'RegistrationTableInsertModal',
      element: 'ToggleGroup',
      key: RegistrationTableInsertType[key],
    },
    id: key,
  })
);

/**
 * A small component that wraps around the choice of either the Legacy RegistrationTable or the Registration
 * Structured Table based upon the `insertTypeSelected`.
 *
 * exported for testing
 */
export function NewOrLegacyRegistrationTableInsert({
  insertTypeSelected,
  setSchemaSelected,
  schemaSelected,
  labelingStrategySelected,
  onChangeLabelingStrategy,
  adapter,
  selectorsById,
  selectedNotation,
  onSelectNotation,
  selectedCustomSyntax,
  onSelectCustomSyntax,
  folderOptionSelector,
  implementedSchemaInterfaces,
}: {
  insertTypeSelected: keyof typeof RegistrationTableInsertType;
  setSchemaSelected: (schema: TagSchema) => void;
  schemaSelected: TagSchema | null;
  labelingStrategySelected: RegistryLabelingStrategy | null;
  onChangeLabelingStrategy: (labelingStrategy: RegistryLabelingStrategy) => void;
  adapter: RegistrationStructuredTableAdapter;
  selectorsById: {[selectorId: string]: SchemaFieldSelector} | null;
  selectedNotation: Notation | null;
  onSelectNotation: (notation: Notation) => void;
  selectedCustomSyntax: string | null;
  onSelectCustomSyntax: (name: string) => void;
  folderOptionSelector: JSX.Element | null;
  implementedSchemaInterfaces: Array<ImplementedStaticSchemaInterfaceGQL>;
}): JSX.Element {
  let labelingStrategyInput: JSX.Element | null = null;
  let notationInput: JSX.Element | null = null;
  let tablePreview: JSX.Element | null = null;

  if (schemaSelected != null) {
    labelingStrategyInput = (
      <SchemaLabelingStrategyInput
        labelingStrategy={labelingStrategySelected}
        onChange={onChangeLabelingStrategy}
        tagSchemaId={schemaSelected != null ? schemaSelected.id : null}
        bannedLabelingStrategies={[FrontendLabelingStrategy.CONVERT_IDS]}
      />
    );
    if (
      [TagSchemaSequenceSchemaType.OLIGO, TagSchemaSequenceSchemaType.RNA_OLIGO].includes(
        schemaSelected.sequenceType!
      ) ||
      (schemaSelected.sequenceType === TagSchemaSequenceSchemaType.RNA_SEQUENCE &&
        BenchlingConfig.MODBIO_ENABLE_SEQUENCES_IN_REG_TABLES)
    ) {
      notationInput = (
        <ResidueNotationDropdown
          notation={selectedNotation!}
          onSelect={onSelectNotation}
          onSelectCustomSyntax={onSelectCustomSyntax}
          selectedCustomSyntax={selectedCustomSyntax}
          componentName="RegistrationTableInsertModal"
        />
      );
    }
  }

  if (schemaSelected != null && labelingStrategySelected != null) {
    const schemaColumnGroup = {...schemaSelected, columnGroup: null};
    tablePreview = (
      <RegistrationTablePreview
        adapter={adapter}
        labelingStrategy={labelingStrategySelected}
        schema={schemaColumnGroup}
        cid="123"
        insertType={insertTypeSelected}
        selectorsById={selectorsById}
        notation={selectedNotation}
        customSyntaxName={selectedCustomSyntax}
        implementedSchemaInterfaces={implementedSchemaInterfaces}
      />
    );
  }

  return (
    <>
      <FormGroup dataTestAttrs={{component: 'FormGroup', element: 'schema'}}>
        <FieldLabel label="Schema" />
        <RegistrationTableTagSchemaSelector
          tableKind={RegistrationTableKind.REGISTRATION_TABLE}
          onChange={setSchemaSelected}
          selectedTagSchemaId={schemaSelected != null ? schemaSelected.id : null}
        />
      </FormGroup>
      <FormGroup dataTestAttrs={{component: 'FormGroup', element: 'labelingStrategyInput'}}>
        {labelingStrategyInput}
      </FormGroup>
      {folderOptionSelector}
      {BenchlingConfig.MOLBIO_ENABLE_MODIFIED_NUCLEOTIDES && <FormGroup>{notationInput}</FormGroup>}
      {tablePreview}
    </>
  );
}

export const REGISTRATION_TABLE_SCHEMA_FIELD_SELECTORS_QUERY: DocumentNode = gql`
  query RegistrationTableSchemaFieldSelectorsQuery @skipAudit @engComponent(name: REGISTRY) {
    schemaFieldSelectors {
      ...SchemaFieldSelectorGraphQL
    }
  }
  ${getFragmentWrapper('SchemaFieldSelector').fragment}
`;

// Teams owning various static schema interfaces may add their own fragments to this query as needed.
export const REGISTRATION_TABLE_SCHEMA_INTERFACES_QUERY: DocumentNode = gql`
  query RegistrationTableSchemaInterfacesQuery($schemaId: StrictString!)
  @skipAudit
  @engComponent(name: REGISTRY) {
    complexPolymerSchemaConfiguration(schemaId: $schemaId) {
      ...ComplexPolymerSchemaConfigurationGraphQL
      ...InaccessibleInfoGraphQL
    }
    implementedAntibodySchemaInterface(schemaId: $schemaId) {
      ... on ImplementedAntibodySchemaInterfaceNode {
        id
        format {
          id
          domains {
            type
            specificity
          }
        }
        schemaInterface {
          id
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('ComplexPolymerSchemaConfiguration').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

function extractComplexPolymerSchemaInterfaces(
  schemaInterfacesQuery: RegistrationTableSchemaInterfacesQuery
): Array<ImplementedStaticSchemaInterfaceGQL> {
  const maybeComplexPolymerInterface = schemaInterfacesQuery.complexPolymerSchemaConfiguration;
  if (maybeComplexPolymerInterface == null || isInaccessible(maybeComplexPolymerInterface)) {
    return [];
  }
  return [maybeComplexPolymerInterface];
}

function extractAntibodySchemaInterfaces(
  schemaInterfacesQuery: RegistrationTableSchemaInterfacesQuery
): Array<ImplementedStaticSchemaInterfaceGQL> {
  const maybeAntibodyInterface = schemaInterfacesQuery.implementedAntibodySchemaInterface;
  if (maybeAntibodyInterface == null || isInaccessible(maybeAntibodyInterface)) {
    return [];
  }
  return [maybeAntibodyInterface];
}

export function extractSchemaInterfaces(
  schemaInterfacesQuery: SkippableBenchlingQueryResult<RegistrationTableSchemaInterfacesQuery>
): Array<ImplementedStaticSchemaInterfaceGQL> {
  if (schemaInterfacesQuery.type === QueryResultType.SUCCESS) {
    return [
      ...extractComplexPolymerSchemaInterfaces(schemaInterfacesQuery.data),
      ...extractAntibodySchemaInterfaces(schemaInterfacesQuery.data),
    ];
  }
  return [];
}

function QueryingRegistrationTableInsertModal(
  props: Omit<
    RegistrationTableInsertModalProps,
    'selectorsById' | 'schemaSelected' | 'setSchemaSelected' | 'implementedSchemaInterfaces'
  >
): JSX.Element {
  const [schemaSelected, setSchemaSelected] = useState<TagSchema | null>(null);

  const implementedSchemaInterfacesQueryResult = useSkippableBenchlingQuery<
    RegistrationTableSchemaInterfacesQuery,
    RegistrationTableSchemaInterfacesQueryVariables
  >(REGISTRATION_TABLE_SCHEMA_INTERFACES_QUERY, {
    variables: {schemaId: schemaSelected?.id ?? ''},
    skip: schemaSelected == null || !isTagSchema(schemaSelected),
  });

  const implementedSchemaInterfaces = extractSchemaInterfaces(implementedSchemaInterfacesQueryResult);

  return (
    <BenchlingQuery<RegistrationTableSchemaFieldSelectorsQuery>
      query={REGISTRATION_TABLE_SCHEMA_FIELD_SELECTORS_QUERY}
      renderLoading={() => (
        <RegistrationTableInsertModal
          {...props}
          selectorsById={null}
          schemaSelected={schemaSelected}
          setSchemaSelected={setSchemaSelected}
          implementedSchemaInterfaces={implementedSchemaInterfaces}
        />
      )}
    >
      {({schemaFieldSelectors}) => {
        let selectorsById: SchemaFieldSelectorsDict = {};
        selectorsById = schemaFieldSelectors.reduce((acc, selector) => {
          acc[selector.id] = selector;
          return acc;
        }, selectorsById);

        return (
          <RegistrationTableInsertModal
            {...props}
            selectorsById={selectorsById}
            schemaSelected={schemaSelected}
            setSchemaSelected={setSchemaSelected}
            implementedSchemaInterfaces={implementedSchemaInterfaces}
          />
        );
      }}
    </BenchlingQuery>
  );
}

/**
 * This RegistrationTableInsertModal inserts a Registration Table which is backed by the
 * StructuredTable framework.  It supports registering new entities or registering existing entities
 * which belong to a particular TagSchema.  There is a third option of registering batches which we
 * then fall back to the old style Registration Table component for.
 */
function RegistrationTableInsertModal(props: RegistrationTableInsertModalProps): JSX.Element {
  const [submitted, setSubmitted] = useState(false);
  const {schemaSelected, setSchemaSelected} = props;
  const [insertTypeSelected, setInsertType] =
    useState<keyof typeof RegistrationTableInsertType>('CREATE_NEW_ENTITIES');

  const enabledLabelingStrategies = getEnabledLabelingStrategies();
  const [labelingStrategySelected, setLabelingStrategy] = useState<RegistryLabelingStrategy | null>(
    schemaSelected != null ? getDefaultLabelingStrategy(enabledLabelingStrategies, [schemaSelected.id]) : null
  );
  const [notation, setNotation] = useState<Notation | null>(null);
  const [customSyntaxName, setCustomSyntaxName] = useState<string | null>(null);
  const {selectedFolderConfig, isInvalidFolderConfig, folderOptionSelector} = useSelectFolderOptionForTable({
    itemTypeSupportsFolders: true,
    isForCreate: insertTypeSelected === 'CREATE_NEW_ENTITIES',
    projectIsRequired: BenchlingConfig.ENABLE_PROJECTS_REQUIRED_FOR_REGISTRY_FILES,
    structuredTableInsertionContext: props.structuredTableInsertionContext,
  });

  function onSelectSchema(schema: TagSchema): void {
    if (
      schema.sequenceType === TagSchemaSequenceSchemaType.OLIGO ||
      schema.sequenceType === TagSchemaSequenceSchemaType.RNA_OLIGO ||
      (schema.sequenceType === TagSchemaSequenceSchemaType.RNA_SEQUENCE &&
        BenchlingConfig.MODBIO_ENABLE_SEQUENCES_IN_REG_TABLES)
    ) {
      setNotation(Notation.NATURAL_BASES);
    } else {
      setNotation(null);
    }
    setSchemaSelected(schema);
  }

  function onChangeInsertType(insertType: keyof typeof RegistrationTableInsertType): void {
    // When changing to a batch-insertion, many options in the dropdown are disabled. This may leave
    // the Insert button incorrectly highlighted. So we reset the selected schema to force the
    // user to manually select.
    if (insertType === 'LEGACY_REGISTER_BATCHES') {
      setSchemaSelected(null);
    }
    setInsertType(insertType);
  }

  const areAllSelectorsLoaded = props.selectorsById != null;
  const onRequestSubmit = (): void => {
    if (submitted) {
      // prevent double clicks
      return;
    }
    setSubmitted(true);
    assert(
      schemaSelected != null &&
        labelingStrategySelected != null &&
        areAllSelectorsLoaded &&
        !isInvalidFolderConfig,
      'The submit button should be disabled unless a schema is selected'
    );
    if (insertTypeSelected !== 'LEGACY_REGISTER_BATCHES') {
      props.onRequestSubmit({
        // The button should be disabled unless a schema is selected
        schema: schemaSelected,
        // The button should be disabled unless a labeling strategy is selected
        labelingStrategy: labelingStrategySelected,
        insertType: RegistrationTableInsertType[insertTypeSelected],
        // The button should be disabled until the selectors are loaded
        selectorsById: props.selectorsById!,
        implementedSchemaInterfaces: props.implementedSchemaInterfaces,
        notation,
        customSyntaxName,
        ...selectedFolderConfig,
      });
    } else {
      assertAndContinue(isBatchesEnabled(), "Batches aren't enabled but we're trying to create them anyway");
      props.onLegacyRequestSubmit({
        tagSchema: schemaSelected,
        tableType: RegistrationTableType.BATCH,
        labelingStrategy: labelingStrategySelected,
        selectorsById: {},
      });
    }
  };

  const validLabelingStrategy =
    schemaSelected &&
    labelingStrategySelected &&
    schemaSelected.labelingStrategies.includes(labelingStrategySelected);

  return (
    <Modal
      title="Insert registration table"
      onRequestClose={props.onRequestClose}
      onRequestSubmit={onRequestSubmit}
      buttons={[
        {
          text: 'Insert',
          submit: true,
          style: ButtonStyle.SECONDARY,
          disabled:
            submitted ||
            !(insertTypeSelected != null && validLabelingStrategy && areAllSelectorsLoaded) ||
            isInvalidFolderConfig,
          dataTestAttrs: {component: 'RegistrationTableInsertModal', key: ModalButtonKey.INSERT},
        },
      ]}
      className="mediocre" // TableEditable's CSS is namespaced under mediocre
    >
      <FormGroup>
        <ToggleGroup<keyof typeof RegistrationTableInsertType>
          options={_TOGGLE_BUTTON_OPTIONS}
          selection={insertTypeSelected}
          onChangeSelection={onChangeInsertType}
          size={ButtonSize.MEDIUM}
        />
      </FormGroup>
      <NewOrLegacyRegistrationTableInsert
        adapter={props.adapter}
        insertTypeSelected={insertTypeSelected}
        setSchemaSelected={onSelectSchema}
        schemaSelected={schemaSelected}
        labelingStrategySelected={labelingStrategySelected}
        onChangeLabelingStrategy={setLabelingStrategy}
        selectorsById={props.selectorsById}
        selectedNotation={notation}
        onSelectNotation={setNotation}
        selectedCustomSyntax={customSyntaxName}
        onSelectCustomSyntax={setCustomSyntaxName}
        folderOptionSelector={folderOptionSelector}
        implementedSchemaInterfaces={props.implementedSchemaInterfaces}
      />
    </Modal>
  );
}

export default QueryingRegistrationTableInsertModal;
